/**
 * auto_trim_resolution デフォルト値の状態遷移 (媒体規格サイズガード §5.3)。
 *
 * デフォルト値は (spec, model) の組で決まるため、次の 3 状態を分けて管理する:
 * - 初期値: getDefaultAutoTrimForSpec(model, spec)
 * - ユーザー手動変更値: settings.auto_trim_resolution (checkbox 操作)
 * - 手動変更済みフラグ: touched (spec 切替でリセット)
 *
 * 適用ルール:
 * - 初回評価 (ページロード): auto_trim_resolution は localStorage に永続化されない
 *   ため undefined = ユーザー未選択。spec が復元されていれば初期値を適用する。
 *   明示値が既に入っている場合 (設定引き継ぎ等) は尊重し、手動扱いにする
 * - spec 切替時: touched をリセットし、(新 spec, 現モデル) の初期値を適用
 * - モデル切替時 (同一 spec): touched なら保持、未操作なら新モデルの初期値を再適用
 *
 * コンポーネント (MediaRequirementsAccordion) から切り出した純関数。
 * effect はこの関数の返す nextValue を settings に反映するだけにする。
 */
import type { AssetSpec } from '../../../features/media/api';
import { getDefaultAutoTrimForSpec } from './aspectRatioSupport';

export type AutoTrimDefaultState = {
  /** 最後に評価した `${specId}::${modelCode}` キー。null = 未評価 (初回) */
  appliedKey: string | null;
  /** 現在の spec 選択中にユーザーが手動でチェックを操作したか */
  touched: boolean;
};

export const INITIAL_AUTO_TRIM_DEFAULT_STATE: AutoTrimDefaultState = {
  appliedKey: null,
  touched: false,
};

export type AutoTrimDefaultResult = {
  state: AutoTrimDefaultState;
  /** settings に反映すべき値。null = 変更しない (現状維持) */
  nextValue: boolean | null;
};

export function evaluateAutoTrimDefault(args: {
  state: AutoTrimDefaultState;
  specId: string | null;
  /** specId が指す spec 実体。ロード中は null (評価を保留する) */
  spec: AssetSpec | null;
  modelCode: string;
  /** settings.auto_trim_resolution の現在値。undefined = ユーザー未選択 */
  currentValue: boolean | undefined;
}): AutoTrimDefaultResult {
  const { state, specId, spec, modelCode, currentValue } = args;

  // spec 実体のロード待ち (id だけ決まって spec が未取得の間は評価しない)
  if (specId && !spec) return { state, nextValue: null };

  const specKey = specId ?? 'none';
  const key = `${specKey}::${modelCode}`;
  if (state.appliedKey === key) return { state, nextValue: null };

  if (state.appliedKey === null) {
    // 初回評価 (ページロード)。media_spec_id は localStorage から復元されるため、
    // 復元 spec に対しても初期値を適用しないと再訪ユーザーに新しい意味論が効かない。
    if (currentValue !== undefined) {
      // 明示値が既に入っている → 尊重し、以降のモデル切替でも保持する
      return { state: { appliedKey: key, touched: true }, nextValue: null };
    }
    if (!spec) return { state: { appliedKey: key, touched: false }, nextValue: null };
    return {
      state: { appliedKey: key, touched: false },
      nextValue: getDefaultAutoTrimForSpec(modelCode, spec),
    };
  }

  const prevSpecKey = state.appliedKey.split('::')[0];
  const specChanged = prevSpecKey !== specKey;
  if (!specChanged && state.touched) {
    // モデル切替のみ & 手動変更済み → 保持
    return { state: { appliedKey: key, touched: true }, nextValue: null };
  }
  // spec 切替 (touched リセット) or 未操作でのモデル切替 → 初期値を適用
  return {
    state: { appliedKey: key, touched: false },
    nextValue: getDefaultAutoTrimForSpec(modelCode, spec),
  };
}

/** checkbox の手動操作を状態に反映する */
export function markAutoTrimTouched(state: AutoTrimDefaultState): AutoTrimDefaultState {
  return state.touched ? state : { ...state, touched: true };
}
